import { Branch, UserAccountsService } from "../gen/clients/llts";
import { useQuery, UseQueryResult } from "react-query";
import { useUserSession } from "./useUserSession";
import { useUserAccount } from "./useUserAccount";

/**
 * Fetches the authenticated user's branch.
 *
 * The branch query is only enabled once `getUserAccount` has completed
 * successfully. This enforces that `getUserAccount` is called first and prevents
 * a race for newly created users, where the branch endpoint would otherwise be
 * hit before the user account exists.
 *
 * All branch observers share the same query key (`["getCurrentUserBranch", username]`)
 * and derive `enabled` from the same account-success state, so they are
 * deduplicated into a single request that stays disabled until the account is ready.
 */
export const useCurrentUserBranch = (): UseQueryResult<Branch | null> => {
  const { username } = useUserSession();
  const { isSuccess: isUserAccountReady } = useUserAccount();
  return useQuery(["getCurrentUserBranch", username], {
    queryFn: getCurrentUserBranchSafe,
    enabled: !!username && isUserAccountReady
  });
};

/**
 * Wrapper around `UserAccountsService.getCurrentUserBranch` that returns `null`
 * when the authenticated user has no associated branch (e.g. ADMIN / SUPER_USER,
 * for which the endpoint returns 401 / 403). Returning `null` instead of letting
 * the error propagate keeps the query cached as a successful response, which
 * prevents the observer from refetching on remount or after a cache clear. Any
 * other error is re-thrown so callers can surface it.
 */
const getCurrentUserBranchSafe = async (): Promise<Branch | null> => {
  try {
    return await UserAccountsService.getCurrentUserBranch();
  } catch (error) {
    return null;
  }
};
